import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Tooltip, useMantineTheme } from '@mantine/core';

interface TTStatsVerifiedProps {
  size?: 'sm' | 'md' | 'lg';
  animate?: boolean;
  className?: string;
  delay?: number;
  lang?: 'en' | 'fr' | 'de' | 'it';
  type?: 'pro' | 'staff';
  showBoth?: boolean; // For staff, show both pro and staff badges
}

const TTStatsVerified: React.FC<TTStatsVerifiedProps> = ({
  size = 'md',
  animate = true,
  className = '',
  delay = 0,
  lang = 'en',
  type = 'pro',
  showBoth = false
}) => {
  const theme = useMantineTheme();
  const primaryColor = theme.colors.blue[6]; // TTStats blue

  const translations = {
    en: {
      pro: 'TTStats Pro verified user',
      staff: 'TTStats Developer'
    },
    fr: {
      pro: 'Utilisateur vérifié TTStats Pro',
      staff: 'Développeur TTStats'
    },
    de: {
      pro: 'Verifizierter TTStats Pro Benutzer',
      staff: 'TTStats Entwickler'
    },
    it: {
      pro: 'Utente verificato TTStats Pro',
      staff: 'Sviluppatore TTStats'
    }
  };

  const tooltipText = type === 'staff' ? translations[lang].staff : translations[lang].pro;
  const sizeClasses = {
    sm: 'w-5 h-5',
    md: 'w-10 h-10 md:w-12 md:h-12',
    lg: 'w-16 h-16'
  };

  // Pro badge (blue checkmark)
  const ProBadgeSVG = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 30 30"
      className={`${sizeClasses[size]} ${className} cursor-help`}
      fill="none"
    >
      <path
        d="M26.97,16.3l-1.57-2.57l0.78-2.91c0.12-0.46-0.1-0.95-0.53-1.15l-2.71-1.32l-0.92-2.87c-0.14-0.46-0.6-0.74-1.07-0.69l-2.99,0.36l-2.32-1.92c-0.37-0.31-0.91-0.31-1.28,0l-2.32,1.92L9.05,4.79C8.58,4.74,8.12,5.02,7.98,5.48L7.06,8.35L4.35,9.67c-0.43,0.2-0.65,0.69-0.53,1.15l0.78,2.91L3.03,16.3c-0.25,0.41-0.17,0.94,0.18,1.27l2.23,2.02l0.07,3.01c0.02,0.48,0.37,0.89,0.84,0.97l2.97,0.49l1.69,2.5c0.27,0.4,0.78,0.55,1.22,0.36L15,25.73l2.77,1.19C17.9,26.97,18.03,27,18.16,27c0.33,0,0.64-0.16,0.83-0.44l1.69-2.5l2.97-0.49c0.47-0.08,0.82-0.49,0.84-0.97l0.07-3.01l2.23-2.02C27.14,17.24,27.22,16.71,26.97,16.3z"
        fill={primaryColor}
      />
      <path
        d="M10.5,15.5 L14,18.5 L19,11.5"
        fill="none"
        stroke="white"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray={animate ? "18" : undefined}
        strokeDashoffset={animate ? "18" : undefined}
      >
        {animate && (
          <animate attributeName="stroke-dashoffset" from="18" to="0" dur="0.4s" begin={`${delay + 0.5}s`} fill="freeze" />
        )}
      </path>
    </svg>
  );

  // Staff badge (gradient with star)
  const StaffBadgeSVG = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 30 30"
      className={`${sizeClasses[size]} ${className} cursor-help`}
      fill="none"
    >
      <defs>
        <linearGradient id="staffGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#8B5CF6">
            <animate attributeName="stop-color" values="#8B5CF6; #EC4899; #F59E0B; #10B981; #3B82F6; #8B5CF6" dur="3s" repeatCount="indefinite" />
          </stop>
          <stop offset="50%" stopColor="#EC4899">
            <animate attributeName="stop-color" values="#EC4899; #F59E0B; #10B981; #3B82F6; #8B5CF6; #EC4899" dur="3s" repeatCount="indefinite" />
          </stop>
          <stop offset="100%" stopColor="#F59E0B">
            <animate attributeName="stop-color" values="#F59E0B; #10B981; #3B82F6; #8B5CF6; #EC4899; #F59E0B" dur="3s" repeatCount="indefinite" />
          </stop>
        </linearGradient>
      </defs>
      <path
        d="M26.97,16.3l-1.57-2.57l0.78-2.91c0.12-0.46-0.1-0.95-0.53-1.15l-2.71-1.32l-0.92-2.87c-0.14-0.46-0.6-0.74-1.07-0.69l-2.99,0.36l-2.32-1.92c-0.37-0.31-0.91-0.31-1.28,0l-2.32,1.92L9.05,4.79C8.58,4.74,8.12,5.02,7.98,5.48L7.06,8.35L4.35,9.67c-0.43,0.2-0.65,0.69-0.53,1.15l0.78,2.91L3.03,16.3c-0.25,0.41-0.17,0.94,0.18,1.27l2.23,2.02l0.07,3.01c0.02,0.48,0.37,0.89,0.84,0.97l2.97,0.49l1.69,2.5c0.27,0.4,0.78,0.55,1.22,0.36L15,25.73l2.77,1.19C17.9,26.97,18.03,27,18.16,27c0.33,0,0.64-0.16,0.83-0.44l1.69-2.5l2.97-0.49c0.47-0.08,0.82-0.49,0.84-0.97l0.07-3.01l2.23-2.02C27.14,17.24,27.22,16.71,26.97,16.3z"
        fill="url(#staffGradient)"
      />
      {/* Code brackets symbol </> */}
      <g fill="white" opacity={animate ? "0" : "1"}>
        {/* Left bracket < */}
        <path d="M12 11 L9 15 L12 19" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        {/* Right bracket > */}
        <path d="M18 11 L21 15 L18 19" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        {/* Forward slash / */}
        <path d="M16 11 L14 19" stroke="white" strokeWidth="2" strokeLinecap="round" fill="none" />
        {animate && (
          <animate attributeName="opacity" from="0" to="1" dur="0.4s" begin={`${delay + 0.5}s`} fill="freeze" />
        )}
      </g>
    </svg>
  );

  const BadgeSVG = type === 'staff' ? StaffBadgeSVG : ProBadgeSVG;

  // For staff showing both badges
  const renderBadges = () => {
    if (type === 'staff' && showBoth) {
      return (
        <div className="flex-shrink-0 inline-flex items-center gap-1" style={{ verticalAlign: 'middle' }}>
          {animate ? (
            <>
              <motion.div
                initial={{ opacity: 0, scale: 0, rotate: -20 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                transition={{
                  duration: 0.5,
                  delay: delay,
                  type: "spring",
                  stiffness: 260,
                  damping: 20
                }}
              >
                {ProBadgeSVG}
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0, rotate: -20 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                transition={{
                  duration: 0.5,
                  delay: delay + 0.1,
                  type: "spring",
                  stiffness: 260,
                  damping: 20
                }}
              >
                {StaffBadgeSVG}
              </motion.div>
            </>
          ) : (
            <>
              {ProBadgeSVG}
              {StaffBadgeSVG}
            </>
          )}
        </div>
      );
    }

    return animate ? (
      <motion.div
        initial={{ opacity: 0, scale: 0, rotate: -20 }}
        animate={{ opacity: 1, scale: 1, rotate: 0 }}
        transition={{
          duration: 0.5,
          delay: delay,
          type: "spring",
          stiffness: 260,
          damping: 20
        }}
        className="flex-shrink-0 inline-flex items-center"
        style={{ verticalAlign: 'middle' }}
      >
        {BadgeSVG}
      </motion.div>
    ) : (
      <div className="flex-shrink-0 inline-flex items-center" style={{ verticalAlign: 'middle' }}>{BadgeSVG}</div>
    );
  };

  const badge = renderBadges();

  const [opened, setOpened] = useState(false);

  const handleClick = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setOpened(!opened);
  };

  // Create a wrapper div with gradient animation for staff tooltips
  const GradientTooltipWrapper = () => {
    return (
      <>
        <style>{`
          @keyframes staffGradientBg {
            0% { background-position: 0% 50%; }
            100% { background-position: 200% 50%; }
          }
          .staff-tooltip {
            background: linear-gradient(90deg, #8B5CF6, #EC4899, #F59E0B, #10B981, #3B82F6, #8B5CF6) !important;
            background-size: 200% 100% !important;
            animation: staffGradientBg 3s linear infinite !important;
          }
        `}</style>
      </>
    );
  };

  return (
    <>
      {type === 'staff' && <GradientTooltipWrapper />}
      <Tooltip
        label={tooltipText}
        position="top"
        withArrow
        opened={opened}
        transitionProps={{ transition: 'fade', duration: 200 }}
        classNames={type === 'staff' ? { tooltip: 'staff-tooltip' } : undefined}
        styles={{
          tooltip: {
            backgroundColor: type === 'staff' ? 'transparent' : primaryColor,
            color: 'white',
            fontSize: '0.875rem',
            padding: '0.5rem 0.75rem',
            borderRadius: '0.5rem'
          }
        }}
      >
        <div
          onMouseEnter={() => setOpened(true)}
          onMouseLeave={() => setOpened(false)}
          onClick={handleClick}
          onTouchEnd={handleClick}
          style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', touchAction: 'manipulation', verticalAlign: 'middle' }}
        >
          {badge}
        </div>
      </Tooltip>
    </>
  );
};

export default TTStatsVerified;
